<template>
  <div class="aruco-client">
    <h2>FingerTip + ROI 테스트 (스냅샷 모드)</h2>

    <div class="controls">
      <button @click="startCamera">카메라 시작</button>
      <button @click="connectWS" :disabled="wsStatus === 'connected'">WebSocket 연결</button>
      <button @click="disconnectWS" :disabled="wsStatus !== 'connected'">WebSocket 종료</button>
      <button @click="onClickCalibrate" :disabled="wsStatus !== 'connected'">ROI 캘리브레이션</button>
      <button @click="onClickCaptureAndSend" :disabled="wsStatus !== 'connected'">손가락 캡처 + F3 전송</button>
    </div>

    <div class="status">
      <p>WS 상태: {{ wsStatus }}</p>
      <p>ROI 사이즈: {{ wsSavedRoiSizeText }}</p>
      <p>마지막 TIP(ROI 좌표): {{ lastTipRoi ? lastTipRoi.join(', ') : '-' }}</p>
      <p>마지막 grid 좌표(서버 계산): {{ lastGrid ? lastGrid.join(', ') : '-' }}</p>

      <!-- F3 페이로드 미리보기 (raw x,y + roi_w,roi_h) -->
      <p v-if="lastPayloadText" style="font-size: 0.9rem; color:#555;">
        F3 전송 payload: {{ lastPayloadText }}
      </p>

      <p v-if="apiResultTip">F3 응답: {{ apiResultTip }}</p>
      <p v-if="apiErrorTip" style="color:red;">F3 에러: {{ apiErrorTip }}</p>
    </div>

    <div class="preview-row">
      <div>
        <h3>로컬 웹캠</h3>
        <video
          ref="video"
          autoplay
          playsinline
          muted
          style="max-width: 320px; border: 1px solid #ccc;"
        ></video>
      </div>

      <div v-if="debugImage">
        <h3>서버 디버그 프레임</h3>
        <img :src="debugImage" style="max-width: 320px; border: 1px solid #ccc;" />
      </div>

      <div v-if="roiPreview">
        <h3>ROI 미리보기</h3>
        <img :src="roiPreview" style="max-width: 320px; border: 1px solid #ccc;" />
      </div>
    </div>

    <canvas ref="canvas" style="display:none;"></canvas>
  </div>
</template>

<script>
import ApiService from '@/services/ApiService';
import {
  connectWS as socketConnectWS,
  disconnectWS as socketDisconnectWS,
  onWS,
  sendJSON,
  isOpen,
} from '@/socket/socket';

export default {
  name: "ArucoClientSnapshot",

  data() {
    return {
      // ws 인스턴스는 socket.js에서 관리하므로 여기서는 상태만 관리
      wsStatus: "disconnected",

      wsSavedRoiSize: null,   // { roi_w, roi_h } 또는 { width, height }
      lastGrid: null,         // 서버에서 계산된 60x40 grid 좌표
      lastTipRoi: null,       // [x_roi, y_roi] (raw ROI 픽셀 좌표)

      debugImage: null,
      roiPreview: null,

      // F3 API 관련 상태
      apiSendingTip: false,
      apiErrorTip: "",
      apiResultTip: "",
      F3_currentMessage: "테스트 메시지입니다.",

      // Tip dedup용
      wsLastTipSig: null,
      F3_sendTipToApi: null,

      // 최근 전송 payload 디버그용
      lastPayloadText: "",

      // 자동 캘리브레이션 여부
      autoCalibrated: false,

      // socket.js 핸들러를 한 번만 등록하기 위한 플래그
      wsHandlersBound: false,
    };
  },

  computed: {
    wsSavedRoiSizeText() {
      if (!this.wsSavedRoiSize) return "-";
      const w = this.wsSavedRoiSize.width || this.wsSavedRoiSize.roi_w;
      const h = this.wsSavedRoiSize.height || this.wsSavedRoiSize.roi_h;
      return `${w} x ${h}`;
    },
  },

  beforeDestroy() {
    this.stopCamera();
    this.disconnectWS();
  },

  methods: {
    // ===== 카메라 =====
    async startCamera() {
      console.log("[Camera] startCamera 호출됨");

      // 이미 켜져 있으면 재요청 안 함
      const video = this.$refs.video;
      if (video && video.srcObject) {
        console.log("[Camera] 이미 실행 중");
        return;
      }

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        console.error("[Camera] getUserMedia 지원 안 함");
        return;
      }

      try {
        const constraints = {
          video: { width: 640, height: 480 },
          audio: false,
        };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        console.log("[Camera] 스트림 획득 성공");

        if (!video) {
          console.error("[Camera] this.$refs.video 없음");
          return;
        }

        video.srcObject = stream;
        video.onloadedmetadata = () => {
          video
            .play()
            .then(() => console.log("[Camera] video 재생 시작"))
            .catch((err) =>
              console.error("[Camera] video.play() 에러:", err.name, err.message)
            );
        };
      } catch (err) {
        console.error("[Camera] getUserMedia 에러:", err.name, err.message, err);
      }
    },

    stopCamera() {
      const video = this.$refs.video;
      if (video && video.srcObject) {
        video.srcObject.getTracks().forEach((t) => t.stop());
        video.srcObject = null;
        console.log("[Camera] 스트림 정지");
      }
    },

    // ===== WebSocket =====
    async connectWS() {
      // 이미 열려 있으면 상태만 동기화
      if (isOpen()) {
        this.wsStatus = "connected";
        return;
      }

      // 1) 먼저 카메라 켜기
      await this.startCamera();

      // 2) WebSocket 상태 초기화
      this.wsStatus = "connecting";
      this.autoCalibrated = false;

      // 3) socket.js 핸들러 바인딩 (한 번만)
      if (!this.wsHandlersBound) {
        this.wsHandlersBound = true;

        onWS('open', () => {
          console.log("✅ WebSocket connected (via socket.js)");
          this.wsStatus = "connected";

          // 연결 직후 자동 캘리브레이션 (1회)
          setTimeout(() => {
            if (!this.autoCalibrated) {
              console.log("[WS] auto calibrate after connect");
              this.onClickCalibrate();
            }
          }, 800);
        });

        onWS('close', () => {
          console.log("🔌 WebSocket closed (via socket.js)");
          this.wsStatus = "disconnected";
          this.autoCalibrated = false;
          this.stopCamera(); // 연결 끊기면 카메라도 종료
        });

        onWS('error', (err) => {
          console.error("WebSocket error (via socket.js):", err);
          this.wsStatus = "error";
        });

        // message는 socket.js에서 JSON.parse 시도 후 넘겨줌
        onWS('message', (payload) => {
          this.onWsMessage(payload);
        });
      }

      // 4) 실제 WebSocket 연결
      //    - 로컬 직접 연결: socketConnectWS("ws://localhost:8765");
      //    - 프록시(/ws-aruco) 사용: socketConnectWS();
      socketConnectWS("ws://localhost:8765");
      // 나중에 프록시 쓰고 싶으면 위 줄을 아래처럼만 바꾸면 됨:
      // socketConnectWS();
    },

    disconnectWS() {
      socketDisconnectWS();
      this.wsStatus = "disconnected";
      this.autoCalibrated = false;
      this.stopCamera(); // 수동 종료에도 카메라 끄기
    },

    // socket.js에서 이미 JSON.parse를 해 준 상태일 수도 있음
    onWsMessage(payload) {
      try {
        let data = payload;
        if (typeof payload === 'string') {
          try {
            data = JSON.parse(payload);
          } catch {
            console.error("onWsMessage: JSON parse 실패, payload:", payload);
            return;
          }
        }

        if (data.type === "calibrate_result") {
          if (data.ok) {
            console.log("ROI 캘리브레이션 성공:", data.roi_w, data.roi_h);

            this.wsSavedRoiSize = {
              width: data.roi_w,
              height: data.roi_h,
              roi_w: data.roi_w,
              roi_h: data.roi_h,
            };

            if (data.roi_image) {
              this.roiPreview = "data:image/jpeg;base64," + data.roi_image;
            }
            if (data.debug_image) {
              this.debugImage = "data:image/jpeg;base64," + data.debug_image;
            }

            // 자동 캘리브 완료 플래그
            this.autoCalibrated = true;
          } else {
            alert("캘리브레이션 실패: " + (data.reason || ""));
          }

        } else if (data.type === "capture_result") {
          if (data.ok) {
            console.log("capture_result:", data);

            this.wsSavedRoiSize = {
              width: data.roi_w,
              height: data.roi_h,
              roi_w: data.roi_w,
              roi_h: data.roi_h,
            };
            this.lastGrid = data.grid || null;

            if (data.debug_image) {
              this.debugImage = "data:image/jpeg;base64," + data.debug_image;
            }

            const tip_roi = data.tip_roi; // [x_roi, y_roi] in ROI pixels
            this.lastTipRoi = Array.isArray(tip_roi) ? tip_roi : null;

            if (this.lastTipRoi) {
              this._sendTipIfNotDup(this.lastTipRoi);
              // 바로 API 호출 (raw tip + roiSize → F3가 정규화)
              this.sendTipToApi(this.lastTipRoi);
            }
          } else {
            alert("손가락 캡처 실패: " + (data.reason || ""));
          }

        } else if (data.type === "error") {
          console.error("서버 에러:", data.reason);
        }
      } catch (e) {
        console.error("onWsMessage 처리 중 에러:", e);
      }
    },

    // ===== 스냅샷 + 전송 =====
    snapshotAndSend(type) {
      if (!isOpen()) {
        console.error("[WS] 연결 안 됨");
        return;
      }
      const video = this.$refs.video;
      const canvas = this.$refs.canvas;
      const ctx = canvas.getContext("2d");

      if (!video || !video.videoWidth || !video.videoHeight) {
        console.error("[Camera] 아직 준비 안 됨");
        return;
      }

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const dataUrl = canvas.toDataURL("image/jpeg", 0.6);
      const base64 = dataUrl.split(",")[1];

      const msg = {
        type, // "calibrate_roi" 또는 "capture_tip"
        image: base64,
      };
      // socket.js를 통해 전송
      sendJSON(msg);
    },

    onClickCalibrate() {
      this.snapshotAndSend("calibrate_roi");
    },

    onClickCaptureAndSend() {
      this.snapshotAndSend("capture_tip");
    },

    // ===== Tip 관련 =====
    _sendTipIfNotDup(tip) {
      const sig = `${tip[0].toFixed(2)},${tip[1].toFixed(2)}`;
      if (sig === this.wsLastTipSig) return;
      this.wsLastTipSig = sig;
      this.F3_sendTipToApi = tip;
    },

    async sendTipToApi(tip) {
      console.log("sendTipToApi(raw ROI tip)", tip);
      if (!tip || tip.length < 2) return;
      if (!this.wsSavedRoiSize || !this.wsSavedRoiSize.width) {
        console.warn("ROI 사이즈 없음, 나중에 재전송 로직 넣을 수 있음");
        return;
      }

      this.apiSendingTip = true;
      this.apiErrorTip = "";
      this.apiResultTip = "";

      try {
        // 여기서 tip은 [x_roi, y_roi] (raw ROI pixel)
        // wsSavedRoiSize는 {width, height, roi_w, roi_h}
        const res = await ApiService.sendFingerTip(
          tip,
          this.wsSavedRoiSize,
          this.F3_currentMessage
        );

        // 디버그용: 실제 payload 형태 미리보기
        const [xRaw, yRaw] = tip;
        const w = this.wsSavedRoiSize.width || this.wsSavedRoiSize.roi_w;
        const h = this.wsSavedRoiSize.height || this.wsSavedRoiSize.roi_h;
        this.lastPayloadText = `x=${Math.round(xRaw)}, y=${Math.round(yRaw)}, roi_w=${w}, roi_h=${h}`;

        console.log("res = await ApiService.sendFingerTip", res);
        const text = res["text"];
        const braille = res["braille"];

        const answer =
          text?.text ||
          text?.message ||
          text?.answer ||
          (typeof text === "string" ? text : "좌표 전송이 완료되었습니다.");

        console.log("API 응답:", answer);
        this.apiResultTip =
          typeof text === "string" ? text : text?.message || "전송 완료";

        // 필요하면 여기서 addMessage/translate 연동
        // this.addMessage('assistant', answer);
        // this.translate(braille);

      } catch (e) {
        this.apiErrorTip = e?.message || String(e);
        console.error("손가락 좌표 전송 실패:", this.apiErrorTip);
      } finally {
        this.apiSendingTip = false;
      }
    },
  },
};
</script>
